import React, { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, Lock, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Persistent, non-dismissible top banner for users with a broken billing state.
 *
 *   past_due   → amber banner  "Your payment failed — X days remaining"
 *   suspended  → red banner    "Account suspended — update card to restore access"
 *
 * While mounted, this component:
 *   • adds `vf-sub-banner-active` class to <body>
 *   • sets CSS var `--vf-banner-h` on <body> to the actual banner height
 * so `index.css` can shift any fixed/sticky `top-0` nav bar down by exactly
 * the right amount on every viewport (mobile stacks the banner, so the
 * height varies).
 */
export default function SubscriptionBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const bannerRef = useRef(null);

  const status = user?.subscription_status;
  const bannerActive = status === 'past_due' || status === 'suspended';

  // Keep body class in sync with mounted state
  useEffect(() => {
    if (!bannerActive) return;
    document.body.classList.add('vf-sub-banner-active');
    return () => {
      document.body.classList.remove('vf-sub-banner-active');
      document.body.style.removeProperty('--vf-banner-h');
    };
  }, [bannerActive]);

  // Keep --vf-banner-h in sync with the banner's rendered height
  useEffect(() => {
    if (!bannerActive) return;
    const el = bannerRef.current;
    if (!el) return;
    const update = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      document.body.style.setProperty('--vf-banner-h', `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [bannerActive]);

  if (!user || !bannerActive) return null;

  const isPastDue = status === 'past_due';
  const graceDays = user.grace_days_left ?? 7;

  const bg = isPastDue
    ? 'bg-gradient-to-r from-amber-500 to-orange-500'
    : 'bg-gradient-to-r from-red-600 to-rose-700';
  const Icon = isPastDue ? AlertTriangle : Lock;

  return (
    <div
      ref={bannerRef}
      className={`${bg} text-white shadow-lg fixed top-0 left-0 right-0 z-[70]`}
      data-testid="subscription-banner"
      data-status={status}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <div className="text-xs sm:text-sm truncate sm:whitespace-normal">
            {isPastDue ? (
              <>
                <span className="font-semibold">Payment failed.</span>{' '}
                <span className="hidden sm:inline">Please update your card. </span>
                {graceDays > 0 && (
                  <span className="opacity-95">
                    <strong>{graceDays} day{graceDays === 1 ? '' : 's'}</strong> left.
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="font-semibold">Account suspended.</span>{' '}
                <span className="opacity-95 hidden sm:inline">Update your card to restore access — your data is intact.</span>
                <span className="opacity-95 sm:hidden">Update card to restore access.</span>
              </>
            )}
          </div>
        </div>

        <button
          onClick={() => navigate('/subscription/update-payment')}
          data-testid="banner-update-card-btn"
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-white text-gray-900 rounded-full font-semibold text-xs sm:text-sm hover:bg-gray-100 transition-colors shadow-sm"
        >
          <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span className="hidden xs:inline sm:inline">Update Card</span>
          <span className="xs:hidden sm:hidden">Update</span>
        </button>
      </div>
    </div>
  );
}
